/**
 * iter347 — ChooseAdToBoost
 *
 * Ad picker inserted between the "Boost Your Ads" CTA (Account →
 * Growth Tips, Wallet → BoostSuggestionCard) and the BoostSelectionModal.
 *
 * Purpose: never open the paid-looking boost package modal before
 * the buyer has picked a specific ad. This surface shows only ads
 * that pass canBoostAd() — same helper the modal, wallet and My Ads
 * page use — so the "boostable" definition stays canonical.
 *
 * No paid entry points. Only earned coins are deducted. All balances
 * (header, My Account, Wallet page, boost modal) come from the
 * canonical AuthContext user.coins field and refresh on success.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Zap, Eye, Sparkles, Plus, Coins, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSmartBack } from '../hooks/useSmartBack';
import { canBoostAd } from '../components/AvailabilityBadge';
import BoostSelectionModal from '../components/BoostSelectionModal';
import './ChooseAdToBoost.css';

const API = process.env.REACT_APP_BACKEND_URL;

function StatusPill({ ad, isAr }) {
  const isBoosted = !!(ad?.is_boosted && ad?.boost_until);
  if (isBoosted) {
    return (
      <span className="cab-pill cab-pill--boost" data-testid="cab-status-boosted">
        <Sparkles size={11} />
        {isAr ? 'مُعزَّز' : 'Boosted'}
      </span>
    );
  }
  return (
    <span className="cab-pill cab-pill--active" data-testid="cab-status-active">
      {isAr ? 'نشط' : 'Active'}
    </span>
  );
}

export default function ChooseAdToBoost() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useSmartBack('/account');
  const { user, token, updateUser } = useAuth();
  const isAr = i18n.language?.startsWith('ar');

  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAd, setSelectedAd] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchAds = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const r = await fetch(`${API}/api/ads/user/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        const all = Array.isArray(data?.ads) ? data.ads : [];
        setAds(all.filter(canBoostAd));
      }
    } catch (e) {
      console.error('[ChooseAdToBoost] fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAds(); }, [fetchAds]);

  // Refresh canonical balance after a successful boost so header,
  // this page and any other mounted view see the deducted amount.
  const handleBoostSuccess = async (payload) => {
    if (payload && typeof payload.new_balance === 'number') {
      try { updateUser({ coins: payload.new_balance }); } catch (_) { /* no-op */ }
    } else {
      // Fallback: refresh from /api/me
      try {
        const r = await fetch(`${API}/api/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) updateUser(await r.json());
      } catch (_) { /* ignored */ }
    }
    // Reload the list so the just-boosted ad flips to the "Boosted" pill.
    fetchAds();
    toast.success(isAr ? 'تم تعزيز الإعلان' : 'Ad boosted');
  };

  const openBoostFor = (ad) => {
    if (!canBoostAd(ad)) {
      toast.info(isAr ? 'هذا الإعلان لا يمكن تعزيزه حالياً' : 'This ad cannot be boosted right now');
      return;
    }
    setSelectedAd(ad);
    setModalOpen(true);
  };

  return (
    <div className="cab-page" dir={isAr ? 'rtl' : 'ltr'} data-testid="choose-ad-to-boost-page">
      <header className="cab-topbar">
        <button
          type="button"
          className="cab-back"
          onClick={goBack}
          aria-label={isAr ? 'رجوع' : 'Back'}
          data-testid="cab-back-btn"
        >
          <ArrowLeft size={20} className={isAr ? 'cab-flip' : ''} />
        </button>
        <div className="cab-title-block">
          <h1 className="cab-title" data-testid="cab-title">
            {isAr ? 'اختر إعلاناً لتعزيزه' : 'Choose an ad to boost'}
          </h1>
          <p className="cab-subtitle">
            {isAr
              ? 'اختر الإعلان الذي تريد تعزيزه لعرض باقات التعزيز.'
              : 'Pick the ad you want to boost — you\u2019ll see boost packages next.'}
          </p>
        </div>
        <div className="cab-balance-pill" data-testid="cab-balance-pill">
          <Coins size={14} />
          <strong>{user?.coins || 0}</strong>
          <span>{'\u00a0'}{t('coins')}</span>
        </div>
      </header>

      <section className="cab-body">
        {loading ? (
          <div className="cab-loading" data-testid="cab-loading">
            <Loader2 size={22} className="animate-spin" />
            <p>{isAr ? 'جارِ التحميل...' : 'Loading your ads…'}</p>
          </div>
        ) : ads.length === 0 ? (
          <div className="cab-empty" data-testid="cab-empty">
            <div className="cab-empty__icon"><Zap size={30} /></div>
            <h3>{isAr ? 'لا توجد إعلانات قابلة للتعزيز' : 'No boostable ads yet'}</h3>
            <p>
              {isAr
                ? 'انشر إعلاناً أولاً ثم عد إلى هنا لتعزيزه.'
                : 'Post an ad first, then come back here to boost it.'}
            </p>
            <button
              type="button"
              className="cab-empty__cta"
              onClick={() => navigate('/add-ad')}
              data-testid="cab-post-ad-cta"
            >
              <Plus size={16} />
              {isAr ? 'انشر إعلاناً' : 'Post an ad'}
            </button>
          </div>
        ) : (
          <ul className="cab-list" data-testid="cab-list">
            {ads.map((ad) => {
              const img =
                (Array.isArray(ad.images) && ad.images[0]) ||
                ad.image_url ||
                ad.cover_image ||
                null;
              const views = ad.metrics?.views ?? ad.views ?? 0;
              return (
                <li
                  key={ad.ad_id || ad._id}
                  className="cab-row"
                  data-testid={`cab-ad-row-${ad.ad_id || ad._id}`}
                >
                  <div className="cab-row__thumb">
                    {img ? (
                      <img src={img} alt={ad.title || ''} loading="lazy" />
                    ) : (
                      <div className="cab-row__thumb--fallback">
                        <Zap size={22} />
                      </div>
                    )}
                  </div>
                  <div className="cab-row__body">
                    <h4 className="cab-row__title" data-testid={`cab-ad-title-${ad.ad_id}`}>
                      {ad.title || (isAr ? 'إعلان بدون عنوان' : 'Untitled ad')}
                    </h4>
                    <div className="cab-row__meta">
                      <StatusPill ad={ad} isAr={isAr} />
                      <span className="cab-views" data-testid={`cab-ad-views-${ad.ad_id}`}>
                        <Eye size={12} />
                        {views} {isAr ? 'مشاهدة' : 'views'}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="cab-row__boost"
                    onClick={() => openBoostFor(ad)}
                    data-testid={`cab-boost-btn-${ad.ad_id}`}
                  >
                    <Zap size={14} />
                    <span>{isAr ? 'عزّز' : 'Boost'}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <BoostSelectionModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedAd(null);
        }}
        ad={selectedAd}
        userCoins={user?.coins || 0}
        onBoostSuccess={handleBoostSuccess}
      />
    </div>
  );
}
